<template>
  <vs-popup
    class="generated-reports"
    id="popup"
    :title="'Generated Reports'"
    @close="handleClose"
    :active.sync="open"
    :buttons-hidden="true"
  >
    <vx-card noShadow>
      <template slot="actions">
        <vs-button
          @click="fetchReports"
          class="mr-0"
          color="success"
          type="border"
          icon-pack="feather"
          icon="icon-rotate-cw"
        >
          Refresh
        </vs-button>
      </template>

      <!-- Auto Refresh Message -->
      <div
        v-if="open"
        class="text-xs text-gray-500 mb-3 text-right italic flex justify-end items-center space-x-1"
      >
        <i class="bx bx-timer"></i>
        <span>Auto-refreshing in {{ refreshCountdown }}s...</span>
      </div>

      <!-- Empty State -->
      <div v-if="reports.length === 0" class="text-center py-8 text-gray-500">
        <i class="bx bx-file-blank text-3xl mb-2 block"></i>
        <p>No reports available</p>
      </div>

      <!-- Report List -->
      <div v-else>
        <div
          v-for="(report, index) in paginatedReports"
          :key="index"
          class="vx-card card-border no-shadow mb-3 flex items-center justify-between px-4 py-3 hover:shadow-md transition-all duration-200 cursor-pointer"
        >
          <div class="flex items-center space-x-3">
            <i class="bx bx-file text-blue-500 text-xl"></i>
            <div>
              <p class="font-semibold text-gray-800 text-sm">
                {{ report.subjectCode || 'Untitled Report' }} <span v-if="report.data.brand" class="uppercase text-warning italic">{{ report.data.brand }}</span>
              </p>
              <p class="text-xs text-gray-500">
                Generated By:
                {{ report.generatedBy.firstName }}
                {{ report.generatedBy.lastName }}
                at {{ __dateTimeFormatter(report.createdAt) }}
              </p>
            </div>
          </div>

          <div>
            <div class="flex">
              <vs-button
                v-if="report.status === 'completed'"
                type="border"
                size="small"
                icon-pack="feather"
                icon="icon-download"
                class="ml-2"
                @click.stop="onClickTemplateView(report.s3Key)"
              >
                DOWNLOAD
              </vs-button>
            </div>
            <vs-chip v-if="report.status === 'pending'" transparent color="#ff9f40">In Progress</vs-chip>
            <vs-chip v-if="report.status === 'failed'" transparent color="danger">Failed</vs-chip>
          </div>
        </div>

        <!-- Pagination -->
        <div class="flex justify-between items-center mt-4 text-sm">
          <span>Page {{ currentPage }} of {{ totalPages }}</span>

          <div class="space-x-2">
            <vs-button
              :disabled="currentPage === 1"
              size="small"
              type="border"
              @click="currentPage--"
              >Prev</vs-button
            >
            <vs-button
              :disabled="currentPage === totalPages"
              size="small"
              type="border"
              @click="currentPage++"
              >Next</vs-button
            >
          </div>
        </div>
      </div>
    </vx-card>
  </vs-popup>
</template>

<script>
import { RepositoryFactory } from '@/repository/RepositoryFactory'
const ReportRepository = RepositoryFactory.get('report')

import SuccessMessage from '@/mixins/SuccessMessage'
import ErrorMessages from '@/mixins/ErrorMessages'

function initialState() {
  return {
    open: false,
    object_name: '',
    reports: [],
    currentPage: 1,
    perPage: 5,
    autoRefreshInterval: null,
    countdownTimer: null,
    refreshCountdown: 5 
  }
}

export default {
  name: 'GeneratedReportsPopUp',
  data() {
    return initialState()
  },
  computed: {
    totalPages() {
      return Math.ceil(this.reports.length / this.perPage)
    },
    paginatedReports() {
      const start = (this.currentPage - 1) * this.perPage
      const end = start + this.perPage
      return this.reports.slice(start, end)
    }
  },
  methods: {
    async openPopup(object_name) {
      try {
        Object.assign(this.$data, initialState())
        this.object_name = object_name
        await this.fetchReports()
        this.open = true
        this.startAutoRefresh()
      } catch (error) {
        this.showError('Failed to open popup.')
      }
    },

    async fetchReports() {
      try {
        const { data } = (await ReportRepository.fetchAllGeneratedReports(this.object_name)).data
        this.reports = data
      } catch (error) {
        this.showError('Failed to load reports.')
      }
    },

    startAutoRefresh() {
      this.clearAutoRefresh()
      this.refreshCountdown = 5

      // Countdown timer (1s)
      this.countdownTimer = setInterval(() => {
        if (this.refreshCountdown > 1) {
          this.refreshCountdown--
        } else {
          this.refreshCountdown = 5
        }
      }, 1000)

      // Auto refresh every 3s
      this.autoRefreshInterval = setInterval(async () => {
        await this.fetchReports()
      }, 5000)
    },

    clearAutoRefresh() {
      if (this.autoRefreshInterval) {
        clearInterval(this.autoRefreshInterval)
        this.autoRefreshInterval = null
      }
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
    },

    handleClose() {
      this.open = false
      this.clearAutoRefresh()
    },

    async onClickTemplateView(key) {
      if(!key) return
      const downloadUrl = `https://ribu-production.s3.ap-southeast-2.amazonaws.com/${key}`
      const newTab = window.open(downloadUrl, '_blank')
      if (!newTab) {
        console.error('Popup blocked. Please allow popups for this site.')
      }
    }
  },
  beforeDestroy() {
    this.clearAutoRefresh()
  },
  mixins: [SuccessMessage, ErrorMessages]
}
</script>

<style scoped>
.generated-reports>>>.vs-popup {
  min-width: 45% !important;
  border-radius: 12px;
}
</style>
